import {css} from "../libs/lit.dist.js";

export default css`
    :host {
        box-sizing: border-box;
        display: flex;
        align-items: end;
        --width: 24px;
        --gap: 2px;
        --icon-border-size: 1px;
        --progress-length: 48px;
        --progress-width: 3.5px;
        --__color-active: var(--color-active, var(--pk-stepper-color-active, var(--pk-color-700)));
        --color-inactive: var(--pk-stepper-color-inactive, var(--bs-gray-400));
        --color: var(--color-inactive);
        --bg-color: var(--pk-stepper-color-background, var(--bs-gray-100));
        --icon-color-active: var(--pk-stepper-icon-color-active, #fff);
        --icon-color-inactive: var(--__color-active);
        --color-label-inactive: var(--pk-stepper-color-label-inactive, var(--bs-gray-600));
        --labels-offset-default: 2.5rem
    }

    :host(:first-of-type) .progress {
        position: relative;
        padding-top: calc((var(--gap) * 2) + var(--width));
        border-radius: var(--width) var(--width) 0 0;
    }

    :host(:first-of-type) .progress:before {
        content: '';
        display: block;
        width: 10px;
        height: 10px;
        background-color: var(--color);
        border-radius: 50%;
        position: absolute;
        top: 0;
        left: 50%;
        translate: -50% 100%;
    }

    :host(:last-of-type) .progress {
        border-radius: 0 0 var(--width) var(--width);
    }

    :host([done]) {
        --color: var(--__color-active);
    }

    :host([done]) .icon {
        --color: var(--__color-active);
        --bg-color: var(--color);
        --icon-color-background: var(--color);
        --border-color: transparent;
        text-align: center;
        color: var(--icon-color-active);
        transition: all 0.2s;
    }

    :host([done]) .icon pk-icon {
        display: inline-block;
        font-size: 0.6rem;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        line-height: var(--width);
    }

    @keyframes step-in {
        0% {scale: 1}
        25% {scale: 1.2}
        100% {scale: 1}
    }

    @keyframes progress-in {
        0% {height: 0;}
        100% {height: 100%;}
    }
    @keyframes progress-in-horizontal {
        0% {width: 0;}
        100% {width: 100%;}
    }

    @keyframes progress-out {
        0% {height: 100%;}
        100% {height: 0;}
    }
    @keyframes progress-out-horizontal {
        0% {width: 100%;}
        100% {width: 0;}
    }

    :host([current]) {
        --color: var(--__color-active);
        color: var(--__color-active);
    }

    :host([current]) .label {
        color: var(--__color-active);
    }
    
    :host([current]) .progress-bar::before {
        animation: var(--animation-progress-in, progress-in) .2s;
        animation-iteration-count: var(--animation-iteration-count, 1);
    }

    :host([current]) .icon {
        position: relative;
        --color: var(--__color-active);
        --border-color: var(--color);

        animation: step-in .33s;
        animation-delay: .185s;
        animation-iteration-count: var(--animation-iteration-count, 1);
    }

    :host([current]) .icon:before {
        content: '';
        width: 5px;
        height: 5px;
        background-color: var(--color);
        border-radius: 50%;
        position: absolute;
        top: 50%;
        left: 50%;
        translate: -50% -50%;
    }

    :host([animate-out]) .progress-bar::before {
        --color: var(--__color-active);
        animation: var(--animation-progress-out, progress-out) .2s;
        animation-iteration-count: 1;
    }

    .progress {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0 var(--gap) var(--gap) var(--gap);
        gap: var(--gap);
        width: var(--width);
        height: calc(var(--progress-length) + var(--width) + var(--gap));
        margin-right: var(--width);
        background-color: var(--bg-color);
    }

    .progress-bar {
        width: var(--progress-width);
        height: var(--progress-bar-length, 100%);
        border-radius: 3px;
        background-color: var(--color-inactive);
        position: relative;
        overflow: hidden;
        z-index: 1;
    }
    
    .progress-bar:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: var(--color);
    }

    .icon {
        position: relative;
        z-index: 5;
        display: block;
        width: calc(var(--width) - (var(--icon-border-size) * 2));
        height: calc(var(--width) - (var(--icon-border-size) * 2));
        background-color: var(--icon-color-background, #fff);
        border: 1px solid var(--border-color, var(--color-inactive));
        flex-shrink: 0;
        border-radius: 50%;
        color: var(--icon-color-inactive);
        transition: all 0.2s;
    }

    .icon pk-icon {
        display: none;
    }

    .label {
        text-transform: uppercase;
        font-size: 0.8rem;
        color: var(--color-label-inactive);
    }

    .content {
        position: relative;
        top: 0.4rem;
        line-height: 1.1rem;
        font-size: 0.9rem;
        max-width: calc(100% - (var(--width) * 2) - (var(--gap) * 2));
    }

    .content .content-label {
        max-width: 100%;
        min-height: 0.75rem;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .content slot {
        white-space: nowrap;
    }
    
    :host(:last-of-type) .content {
        top: 0;
    }

    :host([horizontal]) .progress {
        flex-grow: 1;
        flex-direction: row;
        margin-right: 0;
        padding: var(--gap) 0 var(--gap) var(--gap);
        width: auto;
        height: var(--width);
    }
    
    :host([horizontal]) .progress-bar {
        height: var(--progress-width);
        width: var(--progress-bar-length, 100%);
    }

    :host([horizontal]) {
        flex-grow: 1;
        --animation-progress-in: progress-in-horizontal;
        --animation-progress-out: progress-out-horizontal;
    }

    :host([horizontal]:first-of-type) .progress {
        padding-left: calc((var(--gap) * 2) + var(--width));
        border-radius: var(--width) 0 0 var(--width);
    }

    :host([horizontal]:first-of-type) .progress::before {
        top: 50%;
        left: 0;
        translate: 100% -50%;
    }

    :host([horizontal]:last-of-type) .progress {
        border-radius: 0 var(--width) var(--width) 0;
        padding-right: var(--gap);
    }
    
    :host([labelless]) {
        --labels-offset: 0;
    }    

    :host([labelless]) .content {
        display: none;
    }

    @media (max-width: 768px) {
        :host([horizontal]) .content {
            position: absolute;
            top: 0;
            left: 0;
            opacity: 0;
            transition: opacity 0.2s;
        }
        
        :host([horizontal]) .content .label {
            display: none;
        }

        :host([horizontal][current]) .content {
            opacity: 1;
            transition: opacity 0.2s 0.15s;
        }
    }
    
    @media (min-width: 768px) {
        :host([horizontal]) {
            margin-top: var(--labels-offset, var(--labels-offset-default));
            position: relative;
        }
        :host([horizontal]) .content {
            position: absolute;
            top: calc(0px - var(--labels-offset, var(--labels-offset-default)));
            right: 0;
            text-align: right;
            max-width: calc(100% - 1rem);
        }
    }
`;
